/**
 * @fileoverview Input/output schemas for plot-snapshotter-v2.
 */

import { z } from 'zod';
import { BaseFlowInputSchema } from '@/ai/agents/base-schema';
import { AGENT_SCHEMA_OBJECTS } from '@/ai/agents/prompt-variables';

// =============================================================================
// FLOW INPUT SCHEMA
// =============================================================================

/**
 * Full input for the agent flow.
 *
 * The agent extends the previous chapter's cumulative plot summary rather than
 * re-deriving it from raw chapter text, so the input carries `latestKnownPlotState`
 * (the prior snapshot's plotSummary) plus the last three chapters of prose as a
 * grounding signal. The hook concatenates the agent's delta onto the prior
 * summary before persisting.
 */
export const InputSchema = z.object({
  ...BaseFlowInputSchema.shape,
  ...AGENT_SCHEMA_OBJECTS.storyContext.schema.shape,
  ...AGENT_SCHEMA_OBJECTS.latestKnownPlotState.schema.shape,
  ...AGENT_SCHEMA_OBJECTS.lastThreeChaptersContent.schema.shape,
  ...AGENT_SCHEMA_OBJECTS.chapterContent.schema.shape,
});

export type Input = z.infer<typeof InputSchema>;

// =============================================================================
// OUTPUT SCHEMA
// =============================================================================

export const OutputSchema = z.object({
  currentChapterPlotDevelopments: z.string().describe(
    "A neutral, chronological account of the new plot developments that occur in the CURRENT chapter only. This is appended to the prior cumulative summary by the hook—do not restate earlier events."
  ),
  majorPlotEvents: z.array(z.object({
    id: z.string().describe("Unique identifier for this event."),
    description: z.string().describe("Description of a major plot event in this chapter."),
  })).optional().describe("Key plot events that occurred in this chapter, as distinct from character-level events."),
});

export type Output = z.infer<typeof OutputSchema>;
